import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config/Constants';
import { PLAYER_COLORS } from '../config/Constants';

export class TitleScene extends Phaser.Scene {
  private particles: { x: number; y: number; vx: number; vy: number; size: number; alpha: number; color: number }[] = [];
  private particleGraphics!: Phaser.GameObjects.Graphics;

  constructor() {
    super({ key: 'TitleScene' });
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#050510');

    // Fondo con estrellas/partículas
    this.particleGraphics = this.add.graphics();
    this.particles = [];
    for (let i = 0; i < 80; i++) {
      this.particles.push({
        x: Math.random() * GAME_WIDTH,
        y: Math.random() * GAME_HEIGHT,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        size: 1 + Math.random() * 2,
        alpha: 0.2 + Math.random() * 0.5,
        color: PLAYER_COLORS[Math.floor(Math.random() * PLAYER_COLORS.length)],
      });
    }

    // Título con efecto de brillo
    const titleShadow = this.add.text(GAME_WIDTH / 2 + 4, GAME_HEIGHT * 0.2 + 4, 'VORTEX\nARENA', {
      fontSize: '82px',
      fontFamily: 'Arial Black, Impact, sans-serif',
      color: '#000000',
      align: 'center',
    }).setOrigin(0.5).setAlpha(0.5);

    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT * 0.2, 'VORTEX\nARENA', {
      fontSize: '82px',
      fontFamily: 'Arial Black, Impact, sans-serif',
      color: '#ffffff',
      align: 'center',
      stroke: '#2c3e50',
      strokeThickness: 10,
    }).setOrigin(0.5);

    // Animación de brillo en el título
    this.tweens.add({
      targets: title,
      alpha: { from: 1, to: 0.85 },
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Subtítulo con glitch
    const sub = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT * 0.38, '⚔️  TOP BATTLES  ⚔️', {
      fontSize: '22px',
      fontFamily: 'Arial',
      color: '#f1c40f',
      align: 'center',
      letterSpacing: 8,
    }).setOrigin(0.5);

    this.tweens.add({
      targets: sub,
      scale: { from: 1, to: 1.05 },
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Línea decorativa
    const lineG = this.add.graphics();
    lineG.lineStyle(2, 0x3498db, 0.4);
    lineG.beginPath();
    lineG.moveTo(GAME_WIDTH * 0.2, GAME_HEIGHT * 0.43);
    lineG.lineTo(GAME_WIDTH * 0.8, GAME_HEIGHT * 0.43);
    lineG.strokePath();

    // Botón Partida Rápida
    this.createButton(GAME_WIDTH / 2, GAME_HEIGHT * 0.55, '🎮  PARTIDA RÁPIDA', 0x27ae60, 0x2ecc71, () => {
      this.scene.start('MenuScene', { modo: 'rapida' });
    });

    // Botón Torneo
    this.createButton(GAME_WIDTH / 2, GAME_HEIGHT * 0.64, '🏆  TORNEO', 0x8e44ad, 0x9b59b6, () => {
      this.scene.start('MenuScene', { modo: 'torneo' });
    });

    // Versión
    this.add.text(GAME_WIDTH - 10, GAME_HEIGHT - 10, 'v1.0', {
      fontSize: '12px', color: '#2c3e50',
    }).setOrigin(1, 1);

    // Animaciones decorativas de peonzas alrededor
    this.createOrbitingTops();
  }

  update(): void {
    // Actualizar partículas de fondo
    this.particleGraphics.clear();
    this.particles.forEach((p) => {
      p.x += p.vx;
      p.y += p.vy;
      if (p.x < 0) p.x = GAME_WIDTH;
      if (p.x > GAME_WIDTH) p.x = 0;
      if (p.y < 0) p.y = GAME_HEIGHT;
      if (p.y > GAME_HEIGHT) p.y = 0;
      this.particleGraphics.fillStyle(p.color, p.alpha);
      this.particleGraphics.fillCircle(p.x, p.y, p.size);
    });
  }

  private createButton(x: number, y: number, text: string, color: number, hoverColor: number, onClick: () => void): void {
    const bg = this.add.rectangle(x, y, 360, 56, color, 0.9)
      .setStrokeStyle(2, hoverColor, 0.6)
      .setInteractive({ useHandCursor: true });

    const label = this.add.text(x, y, text, {
      fontSize: '20px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    bg.on('pointerover', () => {
      this.tweens.add({
        targets: [bg, label],
        scaleX: 1.05,
        scaleY: 1.05,
        duration: 150,
        ease: 'Back.easeOut',
      });
      bg.setFillStyle(hoverColor, 1);
      bg.setStrokeStyle(2, 0xffffff, 0.8);
    });

    bg.on('pointerout', () => {
      this.tweens.add({
        targets: [bg, label],
        scaleX: 1,
        scaleY: 1,
        duration: 150,
        ease: 'Sine.easeOut',
      });
      bg.setFillStyle(color, 0.9);
      bg.setStrokeStyle(2, hoverColor, 0.6);
    });

    bg.on('pointerdown', () => {
      this.tweens.add({
        targets: [bg, label],
        scaleX: 0.95,
        scaleY: 0.95,
        duration: 50,
        yoyo: true,
        onComplete: onClick,
      });
    });
  }

  private createOrbitingTops(): void {
    const g = this.add.graphics();
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT * 0.85;
    const orbitR = 120;
    let angle = 0;

    this.time.addEvent({
      delay: 30,
      loop: true,
      callback: () => {
        g.clear();
        angle += 0.02;

        for (let i = 0; i < 3; i++) {
          const a = angle + (i * Math.PI * 2) / 3;
          const x = cx + Math.cos(a) * orbitR;
          const y = cy + Math.sin(a) * orbitR * 0.4;
          const r = 18 + Math.sin(a * 2) * 4;
          const color = PLAYER_COLORS[i % PLAYER_COLORS.length];

          // Sombra
          g.fillStyle(0x000000, 0.2);
          g.fillCircle(x + 2, y + 2, r);

          // Cuerpo
          g.fillStyle(color, 0.8);
          g.fillCircle(x, y, r);
          g.fillStyle(Phaser.Display.Color.ValueToColor(color).darken(30).color, 0.8);
          g.fillCircle(x, y, r * 0.6);
          g.fillStyle(0xffffff, 0.3);
          g.fillCircle(x, y, r * 0.3);
        }
      },
    });
  }
}
